import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getSettings, updateSettings } from '@/lib/api/settings';
import { logActivity as logActivityDb } from '@/lib/api/activity';
import { refreshStore } from '@/lib/store';

interface Settings {
  websiteName: string;
  logoUrl: string;
  faviconUrl: string;
  primaryColor: string;
  accentColor: string;
  secondaryColor: string;
}

const DEFAULT_SETTINGS: Settings = {
  websiteName: 'Esteva Clinic',
  logoUrl: '',
  faviconUrl: '',
  primaryColor: '#c2410c',
  accentColor: '#b45309',
  secondaryColor: '#78716c',
};

export default function SettingsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [settings, setSettings] = useState<Settings>(DEFAULT_SETTINGS);
  const [newPassword, setNewPassword] = useState('');
  const [pwMessage, setPwMessage] = useState('');
  const [pwSuccess, setPwSuccess] = useState(false);
  const [savedMsg, setSavedMsg] = useState('');

  useEffect(() => {
    if (sessionStorage.getItem('admin_logged_in') !== 'true') {
      navigate('/admin');
      return;
    }
    void (async () => {
      try {
        const s = await getSettings();
        if (s) {
          setSettings({
            websiteName: s.website_name,
            logoUrl: s.logo_url,
            faviconUrl: s.favicon_url,
            primaryColor: s.primary_color,
            accentColor: s.accent_color,
            secondaryColor: s.secondary_color,
          });
        }
      } catch { setSettings(DEFAULT_SETTINGS); }
    })();
  }, [navigate]);

  const handleSave = async () => {
    try {
      await updateSettings({
        website_name: settings.websiteName,
        logo_url: settings.logoUrl,
        favicon_url: settings.faviconUrl,
        primary_color: settings.primaryColor,
        accent_color: settings.accentColor,
        secondary_color: settings.secondaryColor,
      });
      await logActivityDb('settings_updated', 'Einstellungen aktualisiert');
      await refreshStore();
      setSavedMsg(t('adm1.settings_saved_live'));
    } catch {
      setSavedMsg(t('adm1.save_error_dot'));
    }
    setTimeout(() => setSavedMsg(''), 3000);
  };

  const handleChangePassword = () => {
    if (newPassword.length < 6) {
      setPwSuccess(false);
      setPwMessage(t('adm1.pw_too_short'));
      return;
    }
    localStorage.setItem('esteva_admin_auth', newPassword);
    setPwSuccess(true);
    setPwMessage(t('adm1.pw_changed'));
    setNewPassword('');
    setTimeout(() => setPwMessage(''), 2000);
  };

  const update = (key: keyof Settings, value: string) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  if (sessionStorage.getItem('admin_logged_in') !== 'true') return null;

  return (
    <div className="px-4 py-6 md:px-6 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="font-heading text-xl font-bold text-foreground-900">{t('adm1.settings_title')}</h1>
          <p className="mt-0.5 text-xs text-foreground-500 font-body">{t('adm1.settings_subtitle')}</p>
        </div>
        <div className="flex items-center gap-3">
          {savedMsg && <span className="text-xs text-green-600 font-label">{savedMsg}</span>}
          <button
            onClick={handleSave}
            className="px-5 py-2 rounded-lg bg-primary-500 text-white text-xs font-label hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            {t('adm1.save')}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* ── General Settings ──────────────────────── */}
        <div className="bg-background-50 rounded-xl border border-background-200/70 overflow-hidden">
          <div className="px-5 py-4 border-b border-background-100">
            <h2 className="font-label text-sm font-semibold text-foreground-900">{t('adm1.settings_general')}</h2>
          </div>
          <div className="p-5 space-y-4">
            <div>
              <label className="block text-xs font-label font-semibold text-foreground-700 mb-1.5">{t('adm1.label_website_name')}</label>
              <input
                type="text"
                value={settings.websiteName}
                onChange={(e) => update('websiteName', e.target.value)}
                className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 font-body focus:outline-none focus:border-primary-400 transition-all"
              />
            </div>
            <div>
              <label className="block text-xs font-label font-semibold text-foreground-700 mb-1.5">{t('adm1.label_logo_url')}</label>
              <input
                type="text"
                value={settings.logoUrl}
                onChange={(e) => update('logoUrl', e.target.value)}
                placeholder="https://..."
                className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 font-body focus:outline-none focus:border-primary-400 transition-all"
              />
            </div>
            <div>
              <label className="block text-xs font-label font-semibold text-foreground-700 mb-1.5">{t('adm1.label_favicon_url')}</label>
              <input
                type="text"
                value={settings.faviconUrl}
                onChange={(e) => update('faviconUrl', e.target.value)}
                placeholder="https://..."
                className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 font-body focus:outline-none focus:border-primary-400 transition-all"
              />
            </div>
          </div>
        </div>

        {/* ── Colors ────────────────────────────────── */}
        <div className="bg-background-50 rounded-xl border border-background-200/70 overflow-hidden">
          <div className="px-5 py-4 border-b border-background-100">
            <h2 className="font-label text-sm font-semibold text-foreground-900">{t('adm1.settings_colors')}</h2>
          </div>
          <div className="p-5 space-y-4">
            <div>
              <label className="block text-xs font-label font-semibold text-foreground-700 mb-1.5">{t('adm1.label_primary_color')}</label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={settings.primaryColor}
                  onChange={(e) => update('primaryColor', e.target.value)}
                  className="w-10 h-10 rounded-lg border border-background-200 cursor-pointer p-0.5"
                />
                <input
                  type="text"
                  value={settings.primaryColor}
                  onChange={(e) => update('primaryColor', e.target.value)}
                  className="flex-1 px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 font-mono focus:outline-none focus:border-primary-400 transition-all"
                />
                <div className="w-8 h-8 rounded-lg border border-background-200" style={{ backgroundColor: settings.primaryColor }}></div>
              </div>
            </div>
            <div>
              <label className="block text-xs font-label font-semibold text-foreground-700 mb-1.5">{t('adm1.label_accent_color')}</label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={settings.accentColor}
                  onChange={(e) => update('accentColor', e.target.value)}
                  className="w-10 h-10 rounded-lg border border-background-200 cursor-pointer p-0.5"
                />
                <input
                  type="text"
                  value={settings.accentColor}
                  onChange={(e) => update('accentColor', e.target.value)}
                  className="flex-1 px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 font-mono focus:outline-none focus:border-primary-400 transition-all"
                />
                <div className="w-8 h-8 rounded-lg border border-background-200" style={{ backgroundColor: settings.accentColor }}></div>
              </div>
            </div>
            <div>
              <label className="block text-xs font-label font-semibold text-foreground-700 mb-1.5">{t('adm1.label_secondary_color')}</label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={settings.secondaryColor}
                  onChange={(e) => update('secondaryColor', e.target.value)}
                  className="w-10 h-10 rounded-lg border border-background-200 cursor-pointer p-0.5"
                />
                <input
                  type="text"
                  value={settings.secondaryColor}
                  onChange={(e) => update('secondaryColor', e.target.value)}
                  className="flex-1 px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 font-mono focus:outline-none focus:border-primary-400 transition-all"
                />
                <div className="w-8 h-8 rounded-lg border border-background-200" style={{ backgroundColor: settings.secondaryColor }}></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Password Change ─────────────────────────── */}
      <div className="bg-background-50 rounded-xl border border-background-200/70 overflow-hidden">
        <div className="px-5 py-4 border-b border-background-100">
          <h2 className="font-label text-sm font-semibold text-foreground-900">{t('adm1.settings_change_password')}</h2>
        </div>
        <div className="p-5">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4 max-w-md">
            <input
              type="password"
              value={newPassword}
              onChange={(e) => { setNewPassword(e.target.value); setPwMessage(''); }}
              placeholder={t('adm1.ph_new_password')}
              className="flex-1 px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 font-body focus:outline-none focus:border-primary-400 transition-all"
            />
            <button
              onClick={handleChangePassword}
              className="px-5 py-2.5 rounded-lg bg-foreground-900 text-white text-xs font-label hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap"
            >
              {t('adm1.settings_change_password')}
            </button>
          </div>
          {pwMessage && (
            <p className={`mt-2 text-xs font-body ${pwSuccess ? 'text-green-600' : 'text-red-500'}`}>
              {pwMessage}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}